import { useState } from 'react'
import { NavLink } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { ChevronDown, LogOut, Menu, User } from 'lucide-react'
import { useAuth } from '@/auth/useAuth'
import BrandMark from '@/components/BrandMark'
import { navigationDomains } from '@/components/layout/_navigation'
import { Button } from '@/components/ui/button'
import { IconButton } from '@novelaide/ui/components/IconButton'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger
} from '@novelaide/ui/components/DropdownMenu'
import { cn } from '@/lib/utils'

export default function TopBar() {
  const { t } = useTranslation()
  const environmentKey = import.meta.env.PROD ? 'production' : 'development'

  return (
    <header className="sticky top-0 z-10 flex h-16 items-center justify-between border-b border-border bg-background/90 px-4 backdrop-blur md:px-5">
      <div className="flex items-center gap-2.5">
        <div className="md:hidden">
          <MobileNavigation />
        </div>
        <BrandMark data-testid="admin-brand-mark" className="size-7" />
        <span className="whitespace-nowrap text-sm font-semibold tracking-tight sm:hidden">
          {t('sidebar.shortTitle')}
        </span>
        <span className="hidden whitespace-nowrap text-sm font-semibold tracking-tight sm:inline">
          {t('sidebar.title')}
        </span>
      </div>
      <div className="flex items-center gap-4 text-xs text-muted-foreground">
        <div
          className="hidden items-center gap-2 sm:flex"
          aria-label={t(`topbar.environment.${environmentKey}`)}
        >
          <span
            className={cn(
              'size-2 rounded-full',
              environmentKey === 'production' ? 'bg-success' : 'bg-warning'
            )}
          />
          <span>{t(`topbar.environment.${environmentKey}`)}</span>
        </div>
        <span className="hidden h-4 w-px bg-border sm:block" aria-hidden="true" />
        <UserMenu />
      </div>
    </header>
  )
}

function MobileNavigation() {
  const { t } = useTranslation()

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <IconButton
          label={t('topbar.actions.openNavigation')}
          size="md"
        >
          <Menu />
        </IconButton>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="max-h-[calc(100vh-5rem)] w-64 overflow-y-auto">
        {navigationDomains.map((domain, domainIndex) => (
          <DropdownMenuGroup key={domain.key}>
            {domainIndex > 0 && <DropdownMenuSeparator />}
            <DropdownMenuLabel className="text-[11px] uppercase tracking-wider text-muted-foreground">
              {t(domain.labelKey)}
            </DropdownMenuLabel>
            {domain.items.map((item) => {
              const Icon = item.icon
              return (
                <DropdownMenuItem key={item.to} asChild>
                  <NavLink to={item.to} className="w-full">
                    <Icon />
                    {t(item.labelKey)}
                  </NavLink>
                </DropdownMenuItem>
              )
            })}
          </DropdownMenuGroup>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  )
}

function UserMenu() {
  const { t } = useTranslation()
  const { admin, logout } = useAuth()
  const [open, setOpen] = useState(false)

  const label = admin?.displayName?.trim() || admin?.username || t('topbar.actions.user')
  // admin.status 是 'active' | 'disabled',跟 zh-CN.json 的 topbar.user.status.* 1:1 映射
  const statusKey = admin ? `topbar.user.status.${admin.status}` : undefined

  return (
    <DropdownMenu open={open} onOpenChange={setOpen}>
      <DropdownMenuTrigger asChild>
        <Button type="button" variant="secondary" size="sm">
          <User />
          {label}
          <ChevronDown className={cn('transition-transform', open && 'rotate-180')} />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-44 p-0 py-1">
        {admin && (
          <div role="presentation" className="border-b border-border px-3 py-2">
            <div className="text-xs font-medium">{admin.displayName || admin.username}</div>
            {statusKey && (
              <div className="text-[10px] uppercase tracking-wider text-muted-foreground">
                {t(statusKey)}
              </div>
            )}
          </div>
        )}
        <DropdownMenuItem
          onSelect={logout}
          className="gap-2 rounded-none px-3 py-1.5 text-xs hover:bg-secondary/50"
        >
          <LogOut />
          {t('topbar.user.menu.logout')}
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  )
}
